import { IDictionary, isNullOrUndefined } from '@bimser/core';
import IconClosePanel from '@bimser/icons/16/close-panel';
import IconMaximizeWindow from '@bimser/icons/16/maximize-window';
import { Modal } from 'antd';
import * as React from 'react';
import { BCButton, findMessage } from '..';
import BCInputSearch from '../BCInputSearch';
import BCLoading from '../BCLoading';
import BCScrollbars from '../BCScrollbars';
import BCSplitModal from './SplitModal';
import Styles from './assets/styles.scss';
import { IModalProps, IModalStickyBar } from './entities';
import { BootstrapWidthType, default as ISplitModalProps } from './entities/ISplitModalProps';

const BCModal = (props: IModalProps) => {
	const stickyBarHeights = React.useRef<IDictionary<number>>({
		top: 0,
		bottom: 0
	});

	const [windowHeight, setWindowHeight] = React.useState<number>(window.innerHeight);
	const [fullScreen, setFullScreen] = React.useState<boolean>(false);
	const [bodyWidth] = React.useState<string>(document.body.style.width.valueOf());

	Styles;

	const updateDimensions = () => {
		setWindowHeight(window.innerHeight);
	};

	React.useEffect(() => {
		setFullScreen(props.canFullScreen);
	}, [props.canFullScreen]);

	React.useEffect(() => {
		window.addEventListener('resize', updateDimensions);
		document.body.style.width = '100%';
		return () => {
			window.removeEventListener('resize', updateDimensions);
			if (bodyWidth?.valueOf?.()) {
				document.body.style.width = bodyWidth.valueOf();
			}
		};
	}, []);

	const renderStickySearchBar = (stickyBar: IModalStickyBar) => {
		if (stickyBar.searchBar?.onSearch) {
			return (
				<BCInputSearch
					placeHolder={findMessage.get('100002')}
					size="middle"
					onChange={v => {
						stickyBar.searchBar.onSearch(v.data);
					}}
				/>
			);
		} else {
			return stickyBar.content;
		}
	};

	const renderModalStickyBar = (position: string) => {
		if (!props.stickyBars) {
			return null;
		}
		const stickyBar = props.stickyBars[position];
		if (!stickyBar) {
			return null;
		}

		if (stickyBar.customStyles?.height) {
			stickyBarHeights.current[position] =
				parseInt((stickyBar.customStyles.height as string).replace('px', '')) + 10;
		} else {
			stickyBarHeights.current[position] = 50;
		}

		return (
			<div
				className={Styles.modalStickyBar}
				style={{ ...stickyBar.customStyles }}
				key={position}
			>
				{renderStickySearchBar(stickyBar)}
			</div>
		);
	};

	const wrapContentWithScrollbars = () => {
		const headerHeight = 61,
			footerHeight = 62;
		const { top, bottom } = stickyBarHeights.current;
		const contentHeight = props.contentHeight
			? props.contentHeight
			: windowHeight - headerHeight - footerHeight - top - bottom;

		return (
			<BCScrollbars
				styles={{
					width: 'auto',
					minHeight: '0',
					maxHeight: `${Math.max(contentHeight, 200)}px`
				}}
			>
				<div
					className={
						Styles.modalContentWrapper +
						' modalContentWrapper' +
						' ' +
						(props.modalContentWrapperCssClass || '')
					}
				>
					{props.children}
				</div>
			</BCScrollbars>
		);
	};

	const calcModalWidth = (): string | number => {
		if (fullScreen) {
			return '100%';
		}

		if (props.width) {
			switch (props.width) {
				case 'small':
					return 400;
				case 'default':
					return 520;
				case 'large':
					return '70%';
				default:
					return props.width;
			}
		}
	};

	const renderFullScreenButton = () => {
		if (props.canFullScreen) {
			return (
				<div
					className={Styles.modalFullScreenButton}
					onClick={() => setFullScreen(!fullScreen)}
				>
					<IconMaximizeWindow />
				</div>
			);
		}
	};

	const renderHeaderButtons = () => {
		if (props.headerButtons?.length) {
			return (
				<div className={Styles.modalHeaderControls}>
					{props.headerButtons.map(buttonProps => (
						<BCButton
							{...buttonProps}
							key={buttonProps.id}
						/>
					))}
				</div>
			);
		}
	};

	const renderHeader = () => {
		const title = (
			<span
				className="modal-title-text"
				title={props.title}
			>
				{props.title}
			</span>
		);

		if (props.renderHeader) {
			return props.renderHeader(title);
		}

		return (
			<>
				{title}
				{renderHeaderButtons()}
				{renderFullScreenButton()}
			</>
		);
	};

	const {
		disableScroll,
		visible,
		afterClose,
		footer,
		handleOk,
		zIndex,
		closable,
		wrapClassName,
		maskClosable = false,
		loading,
		className,
		okText,
		okType,
		cancelText,
		okButtonProps,
		cancelButtonProps,
		destroyOnClose,
		handleCancel,
		bodyStyle,
		keyboard,
		seleniumId,
		afterOpenChange
	} = props;

	const modalClassName = props.width ? '' : 'modalClass';

	return (
		<Modal
			okText={okText || findMessage.get('100004')}
			okType={okType || 'primary'}
			cancelText={cancelText || findMessage.get('100034')}
			okButtonProps={{ ...okButtonProps, size: 'middle' }}
			cancelButtonProps={{ ...cancelButtonProps, size: 'middle' }}
			destroyOnClose={destroyOnClose}
			transitionName={null}
			className={[className, 'modalId_' + props.id, modalClassName].join(' ')}
			centered
			open={visible}
			title={renderHeader()}
			{...(afterClose ? { afterClose } : {})}
			{...(handleOk ? { onOk: handleOk } : {})}
			{...(handleCancel ? { onCancel: handleCancel } : {})}
			width={calcModalWidth()}
			zIndex={zIndex}
			closable={closable}
			wrapClassName={wrapClassName}
			maskClosable={maskClosable}
			mask
			keyboard={isNullOrUndefined(keyboard) ? true : keyboard}
			footer={footer}
			closeIcon={<IconClosePanel />}
			styles={{
				body: bodyStyle
			}}
			data-selenium-id={seleniumId}
			afterOpenChange={afterOpenChange}
			getContainer={props.getContainer}
		>
			<BCLoading
				show={loading}
				delay={300}
			>
				{renderModalStickyBar('top')}
				{disableScroll ? props.children : wrapContentWithScrollbars()}
				{renderModalStickyBar('bottom')}
			</BCLoading>
		</Modal>
	);
};
export default BCModal;
export { BCSplitModal, BootstrapWidthType, IModalProps, IModalStickyBar, ISplitModalProps };
